import { FormikProvider, useFormik } from 'formik'
import { useRouter } from 'next/router'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useMedia } from 'react-use'
import { taskFileAttach } from 'api/tasks'
import { attachTagToTask, deleteTagToTask } from 'api/taskTags'
import { useAddChecklistMutation, api } from 'apiv2'
import { formatToTime } from 'helpers/formatToTime'
import {
  prepareUserList,
  prepareCommonSelects,
  prepareTaskValues,
  prepareUserIdList,
  adaptTaskEstimates,
} from 'helpers/prepares'
import removeDuplicates from 'helpers/removeDuplicates'
import { retrieveProjectSlug } from 'helpers/retrieveProjectSlug'
import { isChecklistAllowedTaskType } from 'helpers/taskTypeAvailability'
import { usePrevilegies } from 'hooks/usePrevilegies'
import { taskTypesSelector, prioritiesSelector } from 'store/dictionaries'
import {
  closeTaskForm,
  closeTemplatesSelectModal,
  taskModalSelector,
  templatesSelectModalSelector,
} from 'store/modals'
import {
  allTasksSelector,
  getAllTasksAction,
  projectInfoSelector,
} from 'store/projects'
import {
  updateTaskStatusSelector,
  createProjectTaskAction,
  updateTaskAction,
  addProjectTaskStatusSelector,
  clearAddTaskStatus,
  createdProjectTaskSelector,
  getTaskDetailAction,
  setEstimateConflictError,
} from 'store/tasks'
import { currentUserSelector } from 'store/users'
import { ScreenSize } from 'styles/constants'
import {
  prepareChecklistsForCreate,
  stripPositionId,
  toFormikChecklists,
} from 'ui/components/checklist/ChecklistForm/helpers'
import ConfirmModal from 'ui/components/modals/ConfirmModal'
import ModalBasic from 'ui/components/modals/ModalBasic'
import { notifyError, notifySuccess } from 'ui/components/Notify'
import { TASK_TYPE, USER_ROLE } from 'utils/constants'
import Form from './components/Form'
// import TaskGradeItems from '../TaskGradeItems'
import TemplatesSelectModal from './components/TemplatesSelectModal'
import { RoleContextProvider } from './context'
import { NotificationLink } from './styled'
import { validationSchema } from './validationSchema'

const { TESTER } = USER_ROLE

const TaskForm = ({}) => {
  const dispatch = useDispatch()
  const router = useRouter()
  const projectSlug = retrieveProjectSlug(router)
  const isMobile = useMedia(ScreenSize.mobile)
  const [files, setFiles] = useState([])
  const [editorResetCounter, setEditorResetCounter] = useState(0)
  const [appliedTemplate, setAppliedTemplate] = useState(null)
  const [addChecklist] = useAddChecklistMutation()

  const {
    defaultType,
    open,
    title,
    defaultTaskDetails,
    eventType,
    openTask,
    stage,
    defaultRelatedBy,
    updateRequest,
    checklistPointTitle,
  } = useSelector(taskModalSelector)

  const isSubtaskModal = title === 'Создать подзадачу' && eventType === 'create'

  const handlerClose = () => {
    dispatch(setEstimateConflictError(null))
    setConfirmOpened(false)
    setAppliedTemplate(null)
    dispatch(closeTaskForm())
    clearAddTaskStatus()
  }
  const taskList = useSelector(allTasksSelector)
  const addProjectTaskStatus = useSelector(addProjectTaskStatusSelector)
  const createdProjectTask = useSelector(createdProjectTaskSelector)

  const priorities = useSelector(prioritiesSelector)
  const taskTypes = useSelector(taskTypesSelector)
  const projectInfo = useSelector(projectInfoSelector)
  const currentUser = useSelector(currentUserSelector)
  const { open: templatesSelectModalOpen } = useSelector(
    templatesSelectModalSelector
  )

  const isTesterInProject =
    projectInfo?.users?.find((u) => u.id === currentUser?.id)?.role?.[0]?.id ===
    TESTER

  // большой props drill 5-6, нужно выносить логику в redux или context
  const userInEstimatesList = defaultTaskDetails?.estimates?.find(
    (el) => el.user_id === currentUser.id
  )

  const taskStatus = useSelector(updateTaskStatusSelector)
  const { isManager, isAdmin } = usePrevilegies()

  const currentUserCanEditEstimate =
    !userInEstimatesList && projectInfo?.perm_user_self_estimate
  const isAdminOrManager = isAdmin || isManager
  const canEditEstimate = isAdminOrManager || currentUserCanEditEstimate
  const canSetSupertask =
    isAdmin || (isManager && projectInfo?.perm_manager_create_supertask)

  const epicIsRequired = !!projectInfo?.epicIsRequired

  const [visibleTaskFields, setVisibleTaskFields] = useState([])

  const [requiredFields, setRequiredFields] = useState({})
  const [pending, setPending] = useState(false)

  const preparedExecutorsList = prepareUserList(
    removeDuplicates(
      projectInfo?.users?.filter((user) => !user.is_deactivated) || []
    )
  )

  const executors = useMemo(() => {
    if (!projectInfo?.perm_user_self_assign && !isManager) {
      return preparedExecutorsList?.filter((i) => i.id !== currentUser?.id)
    }
    return preparedExecutorsList
  }, [preparedExecutorsList, isManager])

  useEffect(() => {
    if (taskStatus === 'error') {
      setPending(false)
    }
    if (taskStatus === 'success') {
      setPending(false)
      formik.resetForm()
      handlerClose()
    }
  }, [taskStatus])

  useEffect(() => {
    if (addProjectTaskStatus === 'success') {
      setPending(false)
      formik.resetForm()
      handlerClose()
      dispatch(api.util.invalidateTags(['KanbanTaskList']))
      notifySuccess(
        <a
          href={`${window.location.origin}/projects/${projectSlug}/${createdProjectTask.data.id}`}
          onClick={(e) => {
            openTask(e, createdProjectTask.data.id)
          }}
        >
          Задача
          <NotificationLink>
            {` #${createdProjectTask.data.id} `}
          </NotificationLink>
          успешно добавлена
          <div>
            <NotificationLink>Открыть</NotificationLink>
          </div>
        </a>
      )
    }
    if (addProjectTaskStatus === 'error') {
      setPending(false)
    }
  }, [addProjectTaskStatus])

  const startRelations = {
    block:
      defaultTaskDetails?.block?.length > 0
        ? prepareCommonSelects(
            taskList?.filter((i) =>
              defaultTaskDetails?.block?.map((i) => i.id).includes(i.id)
            )
          )
        : [],
    related:
      defaultTaskDetails?.related?.length > 0
        ? prepareCommonSelects(
            taskList?.filter((i) =>
              defaultTaskDetails?.related?.map((i) => i.id).includes(i.id)
            )
          )
        : [],
    release_id: defaultTaskDetails?.release?.id
      ? prepareCommonSelects(
          taskList?.filter((i) => i.id === defaultTaskDetails?.release.id)[0]
        )
      : '',
    epic_id: defaultTaskDetails?.epic?.id
      ? prepareCommonSelects(
          taskList?.find((i) => i.id === defaultTaskDetails?.epic.id)
        )
      : defaultTaskDetails?.task_type.id === TASK_TYPE.Epic
        ? prepareCommonSelects(
            taskList?.find((i) => i.id === defaultTaskDetails?.id)
          )
        : '',
  }

  const isAction = (...taskActionsToCheck) =>
    taskActionsToCheck.some((action) => action === eventType)
  const isNotAction = (...taskActionsToCheck) =>
    taskActionsToCheck.every((action) => action !== eventType)
  const isCreateSubtask = () =>
    title !== 'Создать подзадачу' && isAction('edit')

  const newtaskReleated = taskList?.filter(
    (task) => task.id === defaultRelatedBy
  )

  const checkEvent = (value) => {
    if (
      isAction('copy', 'bug') ||
      defaultTaskDetails?.[value] == 0 ||
      !defaultTaskDetails?.[value]
    ) {
      return ''
    }

    const val = formatToTime(defaultTaskDetails?.[value]).replace(/\s/g, '')
    if (val == 0) return undefined
    return val
  }

  const createTask = async (
    preparedValues,
    executorsIdList,
    observersIdList,
    blockIds,
    relatedIds,
    typeId,
    dates,
    tagsId,
    checklists
  ) => {
    try {
      const resp = await dispatch(
        createProjectTaskAction({
          slug: projectInfo.slug,
          values:
            typeId === TASK_TYPE.Epic
              ? {
                  ...preparedValues,
                  executors: [],
                  component_id: undefined,
                  estimate_cost: undefined,
                  estimate_worker: undefined,
                  block: undefined,
                  related: undefined,
                  dev_link: undefined,
                  markup_link: undefined,
                  layout_link: undefined,
                  observers: [],
                  estimate_cost_managemen: undefined,
                  release_id: undefined,
                  epic_id: undefined,
                  related: [],
                }
              : {
                  ...preparedValues,
                  executors: executorsIdList || [],
                  observers: observersIdList || [],
                  block: blockIds,
                  related: relatedIds,
                },
        })
      ).unwrap()

      const taskId = resp?.data?.id
      if (!taskId) return

      const allPromises = []

      const filePromises =
        Array.isArray(files) && files.length
          ? files.map((file) =>
              taskFileAttach(taskId, file.id).catch(() => {
                notifyError('Ошибка загрузки файлов')
              })
            )
          : []

      allPromises.push(...filePromises)

      const tagPromises =
        Array.isArray(tagsId) && tagsId.length && typeId !== TASK_TYPE.Epic
          ? tagsId.map((tagId) =>
              attachTagToTask(taskId, tagId).catch(() => {
                notifyError('Ошибка добавления тега')
              })
            )
          : []

      allPromises.push(...tagPromises)

      if (checklists?.length) {
        const checklistPromise = addChecklist({
          taskId,
          data: { items: prepareChecklistsForCreate(checklists) },
        })
          .unwrap()
          .catch(() => {
            notifyError(
              `Не удалось сохранить ${checklists.length > 1 ? 'чек-листы' : 'чек-лист'}`
            )
          })

        allPromises.push(checklistPromise)
      }

      await Promise.allSettled(allPromises)

      dispatch(getTaskDetailAction(taskId))
    } finally {
      updateRequest?.()
    }
  }

  const updateTask = async (
    preparedValues,
    executorsIdList,
    observersIdList,
    blockIds,
    relatedIds,
    typeId,
    dates,
    tagIdsToUpdate,
    tagIdsToDelete,
    checklists
  ) => {
    try {
      await dispatch(
        updateTaskAction({
          id: defaultTaskDetails?.id,
          values:
            typeId === TASK_TYPE.Epic
              ? {
                  ...preparedValues,
                  epic_id: undefined,
                  executors: [],
                  components: [],
                  block: null,
                  related: null,
                  ...dates,
                }
              : {
                  ...preparedValues,
                  block: blockIds,
                  related: relatedIds,
                  executors: executorsIdList || [],
                  observers: observersIdList || [],
                  ...dates,
                },
        })
      ).unwrap()

      const allPromises = []

      const attachPromises =
        Array.isArray(tagIdsToUpdate) && tagIdsToUpdate.length
          ? tagIdsToUpdate.map((tagId) =>
              attachTagToTask(defaultTaskDetails?.id, tagId).catch(() => {
                notifyError('Ошибка добавления тега')
              })
            )
          : []

      allPromises.push(...attachPromises)

      const deletePromises =
        Array.isArray(tagIdsToDelete) && tagIdsToDelete.length
          ? tagIdsToDelete.map((tagId) =>
              deleteTagToTask(defaultTaskDetails?.id, tagId).catch(() => {
                notifyError('Ошибка удаления тега')
              })
            )
          : []

      allPromises.push(...deletePromises)

      if (checklists?.length && defaultTaskDetails?.id) {
        const checklistPromise = addChecklist({
          taskId: defaultTaskDetails.id,
          data: { items: stripPositionId(checklists) },
        })
          .unwrap()
          .catch(() => {
            notifyError(
              `Не удалось сохранить ${checklists.length > 1 ? 'чек-листы' : 'чек-лист'}`
            )
          })

        allPromises.push(checklistPromise)
      }

      await Promise.allSettled(allPromises)

      dispatch(getTaskDetailAction(defaultTaskDetails.id))
    } finally {
      updateRequest?.()
    }
  }

  const initialTaskTypeId = useMemo(() => {
    if (!isNotAction('edit', 'copy')) {
      return defaultType ?? defaultTaskDetails?.task_type?.id
    }

    if (isTesterInProject) return TASK_TYPE.Bug

    if (defaultType != null) return defaultType

    return defaultTaskDetails?.task_type?.id
  }, [defaultType, isTesterInProject, defaultTaskDetails?.task_type?.id])

  const initialTaskTypeOption = useMemo(() => {
    return prepareCommonSelects(
      taskTypes?.find((t) => t.id === initialTaskTypeId) || taskTypes?.[1]
    )
  }, [taskTypes, initialTaskTypeId])

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name:
        (title === 'Создать подзадачу' &&
        eventType === 'create' &&
        checklistPointTitle
          ? checklistPointTitle
          : undefined) ??
        (isAction('create') ? '' : (defaultTaskDetails?.name ?? '')),
      stage_id: isAction('edit') ? defaultTaskDetails?.stage?.id : '1',
      task_type_id: initialTaskTypeOption,
      //estimate_worker: checkEvent('estimate_worker'),
      estimate_cost: checkEvent('estimate_cost'),
      executors:
        isCreateSubtask() || isAction('bug')
          ? prepareUserList(defaultTaskDetails?.users)
          : [],
      observers: prepareUserList(defaultTaskDetails?.observers) || undefined,
      component_id:
        defaultTaskDetails?.component && (isCreateSubtask() || isAction('bug'))
          ? prepareCommonSelects(defaultTaskDetails?.component)
          : undefined,
      priority_id: defaultTaskDetails?.priority
        ? prepareCommonSelects(defaultTaskDetails?.priority)
        : prepareCommonSelects(priorities[1]),
      layout_link: defaultTaskDetails?.layout_link
        ? defaultTaskDetails?.layout_link
        : undefined,
      tags: defaultTaskDetails?.tags ? defaultTaskDetails?.tags : [],
      tags_to_update: [],
      tags_to_delete: [],
      markup_link: defaultTaskDetails?.markup_link
        ? defaultTaskDetails?.markup_link
        : undefined,
      dev_link: defaultTaskDetails?.dev_link
        ? defaultTaskDetails?.dev_link
        : undefined,
      description: isSubtaskModal
        ? ''
        : isNotAction('edit', 'copy')
          ? ''
          : defaultTaskDetails?.description || '',
      //svyazi
      block: startRelations.block || undefined,
      related: defaultRelatedBy
        ? prepareCommonSelects(newtaskReleated)
        : startRelations.related || undefined,
      release_id: startRelations.release_id || undefined,
      epic_id: startRelations.epic_id || undefined,

      date_start: isAction('create', 'bug')
        ? undefined
        : defaultTaskDetails?.date_start || undefined,
      date_end: isAction('create', 'bug')
        ? undefined
        : defaultTaskDetails?.date_end || undefined,
      deadline: isNotAction('edit')
        ? undefined
        : defaultTaskDetails?.deadline || undefined,
      estimates: isAction('create', 'copy', 'bug')
        ? []
        : adaptTaskEstimates(defaultTaskDetails?.estimates ?? []),
      is_overtime: !canEditEstimate
        ? undefined
        : isAction('create', 'copy', 'bug')
          ? 0
          : (defaultTaskDetails?.is_overtime ?? 0),
      is_supertask: !canSetSupertask
        ? undefined
        : isAction('create', 'copy', 'bug')
          ? 0
          : (defaultTaskDetails?.is_supertask ?? 0),
      estimate_cost_management: checkEvent('estimate_cost_management'),
      estimate_cost_test: checkEvent('estimate_cost_test'),
      checklists: defaultTaskDetails?.checklist?.length
        ? toFormikChecklists(defaultTaskDetails?.checklist, {
            asNew: isAction('copy'),
          })
        : [],
    },
    validationSchema: validationSchema({
      requiredFields,
      visibleTaskFields,
      epicIsRequired,
    }),
    onSubmit: async (values) => {
      const editingIndexes = (values.checklists ?? [])
        .map((cl, i) => (cl?.__editing ? i : -1))
        .filter((i) => i >= 0)

      if (editingIndexes.length > 0) {
        const badIndexes = Array.from(new Set([...editingIndexes]))
        badIndexes.forEach((idx) => {
          const namePath = `checklists.${idx}.name`
          formik.setFieldTouched(namePath, true, false)
          formik.setFieldError(
            namePath,
            'Завершите редактирование названия чек-листа'
          )
        })
        return
      }
      const preparedValues = prepareTaskValues(
        values,
        eventType,
        stage,
        isAdminOrManager
      )

      const executorsIdList = prepareUserIdList(preparedValues.executors)
      const observersIdList = prepareUserIdList(preparedValues.observers)
      const blockIds = values.block?.map((i) => i.id)
      const relatedIds = values.related?.map((i) => i.id)
      const typeId = values.task_type_id.id
      const dates = {
        date_start: values.date_start || undefined,
        date_end: values.date_end || undefined,
      }
      const tagsId = values.tags?.map((i) => i.id)
      const tagIdsToUpdate = values.tags_to_update
      const tagIdsToDelete = values.tags_to_delete
      const checklists = isChecklistAvailable ? values.checklists : []

      if (isAdmin || isManager) {
        Object.defineProperty(dates, 'deadline', {
          value: values.deadline,
        })
      }

      const isCreatingSubtask =
        isAction('create') &&
        !!defaultTaskDetails?.id &&
        !defaultTaskDetails?.parent &&
        defaultTaskDetails?.task_type?.id !== TASK_TYPE.Epic

      const isCreatingEpicTask =
        isAction('create') &&
        !!defaultTaskDetails?.id &&
        !!defaultTaskDetails?.epic_by

      if (isCreatingSubtask) {
        preparedValues.parent_id = defaultTaskDetails.id
      }

      if (isAction('copy')) {
        preparedValues.children = defaultTaskDetails.children
      }

      setPending(true)
      isAction('edit')
        ? updateTask(
            preparedValues,
            executorsIdList,
            observersIdList,
            blockIds,
            relatedIds,
            typeId,
            dates,
            tagIdsToUpdate,
            tagIdsToDelete,
            checklists
          )
        : await createTask(
            preparedValues,
            executorsIdList,
            observersIdList,
            blockIds,
            relatedIds,
            typeId,
            dates,
            tagsId,
            checklists
          )

      if (isCreatingSubtask || isCreatingEpicTask) {
        dispatch(getTaskDetailAction(defaultTaskDetails.id))
      }
    },
  })

  const isSubtaskMode =
    title === 'Создать подзадачу' || !!defaultTaskDetails?.parent

  const canManageChecklist =
    (isAction('create') &&
      projectInfo?.capabilities?.includes('create_tasks')) ||
    (isAction('bug') &&
      defaultTaskDetails?.capabilities?.includes('create_bug')) ||
    (isAction('edit', 'copy') &&
      defaultTaskDetails?.capabilities?.includes('edit_task'))

  const isChecklistAvailable =
    isChecklistAllowedTaskType(formik.values.task_type_id?.id) &&
    !isSubtaskMode &&
    (isManager || isAdmin || canManageChecklist)

  useEffect(() => {
    if (checklistPointTitle) {
      formik.setFieldValue('component_id', null, false)
    }
  }, [checklistPointTitle])

  useEffect(() => {
    setVisibleTaskFields(
      projectInfo?.flow?.task_type_fields_map?.find(
        (i) =>
          i.task_type_id ===
          (formik?.values.task_type_id?.id >= 0
            ? formik.values.task_type_id.id
            : defaultTaskDetails?.task_type?.id)
      )?.fields
    )
    handleChangeTaskType(formik?.values.task_type_id?.id)
  }, [projectInfo?.slug, formik?.values.task_type_id?.id])

  const lastOpenedTaskRef = useRef()

  useEffect(() => {
    if (open) {
      dispatch(getAllTasksAction(projectSlug))
      lastOpenedTaskRef.current = defaultTaskDetails
    } else {
      formik.resetForm()
      setFiles([])
      setAppliedTemplate(null)
      if (lastOpenedTaskRef.current?.id && formik.dirty) {
        dispatch(getTaskDetailAction(lastOpenedTaskRef.current?.id))
      }
      lastOpenedTaskRef.current = null
    }
  }, [open])

  const handleChangeTaskType = (taskTypeId) => {
    if (taskTypeId === TASK_TYPE.Epic) {
      formik.setFieldValue('checklists', [])
    }

    switch (taskTypeId) {
      // Эпик
      case TASK_TYPE.Epic:
        setRequiredFields({
          component_id: false,
          description: false,
        })
        break
      // Бэклог
      case TASK_TYPE.Backlog:
        setRequiredFields({
          component_id: false,
        })
        break
      default:
        setRequiredFields({})
        break
    }
  }

  const startDateHasValue = Boolean(formik.getFieldMeta('date_start').value)

  useEffect(() => {
    if (!startDateHasValue) {
      formik.setFieldValue('date_end', undefined)
    }
  }, [startDateHasValue])
  const [confirmOpened, setConfirmOpened] = useState(false)

  useEffect(() => {
    if (open && isSubtaskModal) {
      formik.setFieldValue('description', '', false)

      setEditorResetCounter((c) => c + 1)

      requestAnimationFrame(() => {
        const nameInput = document.getElementById('editNameInput')
        nameInput?.focus?.()
      })
    }
  }, [open, isSubtaskModal])

  const applyDescriptionTemplate = ({ description, preparedChecklists }) => {
    const checklists = isChecklistAvailable ? preparedChecklists : []

    formik.setFieldValue('description', description, true)
    formik.setFieldValue('checklists', checklists, true)

    setAppliedTemplate({ description, checklists: checklists })
    setEditorResetCounter((c) => c + 1)
  }

  return (
    <ModalBasic
      size={isMobile ? 'fullscreen' : 'large'}
      title={title}
      open={open}
      handlerClose={() => setConfirmOpened(true)}
      onRouteChangeClose={handlerClose}
    >
      <RoleContextProvider
        taskEditPrivileges={{
          canSetSupertask,
          canEditEstimate,
          currentUserCanEditEstimate,
          isAdminOrManager,
        }}
      >
        <FormikProvider value={formik}>
          <Form
            formik={formik}
            executors={executors}
            observers={preparedExecutorsList}
            pending={pending}
            visibleTaskFields={visibleTaskFields}
            requiredFields={requiredFields}
            files={files}
            setFiles={setFiles}
            setConfirmOpened={setConfirmOpened}
            isSubtask={!!defaultTaskDetails?.parent}
            editorResetCounter={editorResetCounter}
            isChecklistAvailable={isChecklistAvailable}
          />
        </FormikProvider>
      </RoleContextProvider>
      <div>
        <ConfirmModal
          title="Закрыть окно?"
          open={confirmOpened}
          confirmHandler={handlerClose}
          handlerClose={() => setConfirmOpened(false)}
        />
      </div>
      <div>
        <TemplatesSelectModal
          descriptionValue={formik.values.description}
          checklistsValue={formik.values.checklists}
          appliedTemplate={appliedTemplate}
          applyDescriptionTemplate={applyDescriptionTemplate}
          open={templatesSelectModalOpen}
          handlerClose={() => {
            dispatch(closeTemplatesSelectModal())
          }}
        />
      </div>
    </ModalBasic>
  )
}

export default TaskForm
